import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Mic, Info, RefreshCw, CheckCircle,
  Bluetooth, BluetoothConnected, Heart, Zap, Activity
} from "lucide-react";
import { toast } from "sonner";
import { useBiofeedbackContext } from "../contexts/BiofeedbackContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  surface:  "#0f1114",
  surface2: "#161a1f",
  border:   "rgba(255,255,255,0.06)",
  border2:  "rgba(255,255,255,0.11)",
  text:     "#e8e2d6",
  muted:    "rgba(237,232,223,0.68)",
  dim:      "rgba(237,232,223,0.38)",
  ground:   "#4CAF7A",
  observe:  "#4A90D9",
  connect:  "#C07FD9",
  lock:     "#C9A84C",
  danger:   "#D94A4A",
};

// ─── PRACTICE DEFINITIONS ────────────────────────────────────────────────────
// Three daily breath checks. Composite score = weighted average 1-100.
const PRACTICES = [
  {
    id:          "kapalbhati",
    name:        "Kapalbhati",
    label:       "Skull-Shining Breath",
    color:       T.observe,
    weight:      0.40,
    duration:    30,
    instruction: "Rapid forceful exhales through nose. Let inhales be passive. Maintain steady rhythm.",
    measureNote: "Measuring rhythm consistency and cycle rate",
  },
  {
    id:          "nadi_shodhana",
    name:        "Nadi Shodhana",
    label:       "Alternate Nostril",
    color:       T.observe,
    weight:      0.35,
    duration:    40,
    instruction: "4-count inhale left, hold, 4-count exhale right. Then inhale right, hold, exhale left.",
    measureNote: "Measuring inhale/exhale ratio and cycle symmetry",
  },
  {
    id:          "bhramari",
    name:        "Bhramari",
    label:       "Humming Bee Breath",
    color:       T.connect,
    weight:      0.25,
    duration:    35,
    instruction: "Deep inhale, then exhale with sustained humming sound. Feel vibration in skull.",
    measureNote: "Measuring sustained humming duration and pitch consistency",
  },
];

const MS = { IDLE: "idle", INTRO: "intro", ACTIVE: "active", DONE: "done" };

// ─── SCORING ─────────────────────────────────────────────────────────────────
const computeScore = (id, { duration, avgLevel, consistency, cycles }) => {
  if (duration < 1) return 20;
  if (id === "kapalbhati") {
    return Math.round(Math.max(15, Math.min(100,
      Math.min(100, (cycles / 20) * 60) + consistency * 40
    )));
  }
  if (id === "nadi_shodhana") {
    return Math.round(Math.max(15, Math.min(100,
      Math.min(60, (duration / 40) * 60) + (avgLevel / 40) * 40
    )));
  }
  if (id === "bhramari") {
    return Math.round(Math.max(15, Math.min(100,
      Math.min(60, (duration / 25) * 60) + consistency * 40
    )));
  }
  return 50;
};

const computeComposite = (scores) => {
  const weights = { kapalbhati: 0.40, nadi_shodhana: 0.35, bhramari: 0.25 };
  let total = 0, wSum = 0;
  Object.entries(weights).forEach(([id, w]) => {
    if (scores[id]) { total += scores[id] * w; wSum += w; }
  });
  return wSum > 0 ? Math.round(total / wSum) : null;
};

// ─── HERO RING ───────────────────────────────────────────────────────────────
const HeroRing = ({ score }) => {
  const size = 160, sw = 10, r = (size - sw) / 2;
  const circ = 2 * Math.PI * r;
  const offset = score ? circ - (score / 100) * circ : circ;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <defs>
          <linearGradient id="heroGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor={T.observe} />
            <stop offset="50%"  stopColor={T.connect} />
            <stop offset="100%" stopColor={T.lock} />
          </linearGradient>
        </defs>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border2} strokeWidth={sw} />
        {score && (
          <circle cx={size/2} cy={size/2} r={r} fill="none"
            stroke="url(#heroGrad)" strokeWidth={sw} strokeLinecap="round"
            strokeDasharray={circ} strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.8s ease-out" }} />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 42, color: score ? T.text : T.dim, lineHeight: 1 }}>
          {score || "-"}
        </span>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: T.lock,
          letterSpacing: "0.18em", textTransform: "uppercase", marginTop: 4 }}>
          Capacity Score
        </span>
      </div>
    </div>
  );
};

// ─── PRACTICE ROW ────────────────────────────────────────────────────────────
const PracticeRow = ({ practice, score, isActive, isRunning, onClick }) => (
  <div
    className="flex items-center gap-4 py-3 px-4 rounded-xl cursor-pointer transition-all"
    style={{
      background: isActive ? `${practice.color}0d` : "transparent",
      border: `1px solid ${isActive ? practice.color + "33" : "transparent"}`,
      opacity: isRunning && !isActive ? 0.4 : 1,
    }}
    onClick={onClick}
  >
    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: practice.color }} />
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: T.text, fontWeight: 500 }}>
          {practice.name}
        </span>
        <span style={{ fontFamily: "Instrument Sans, sans-serif", fontSize: 11, color: T.muted }}>
          {practice.label}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full overflow-hidden" style={{ background: T.border2 }}>
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${score || 0}%`, backgroundColor: practice.color }} />
      </div>
    </div>
    <div className="text-right flex-shrink-0" style={{ minWidth: 48 }}>
      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 15, color: score ? practice.color : T.dim }}>
        {score || "-"}
      </span>
      {score && (
        <span style={{ display: "block", fontFamily: "'DM Mono', monospace", fontSize: 9, color: T.dim }}>
          /100
        </span>
      )}
    </div>
  </div>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export const Baseline = ({ user }) => {
  const [activePractice, setActivePractice] = useState(null);
  const [measureState, setMeasureState]     = useState(MS.IDLE);
  const [countdown, setCountdown]           = useState(3);
  const [timeLeft, setTimeLeft]             = useState(0);
  const [audioLevel, setAudioLevel]         = useState(0);
  const [scores, setScores]                 = useState({});
  const [compositeScore, setCompositeScore] = useState(null);
  const [lastFullTest, setLastFullTest]     = useState(null);
  const [loading, setLoading]               = useState(true);
  const [sessionId]                         = useState(`session_${Date.now()}`);

  const biofeedback = useBiofeedbackContext();

  const audioCtxRef  = useRef(null);
  const analyserRef  = useRef(null);
  const streamRef    = useRef(null);
  const rafRef       = useRef(null);
  const timerRef     = useRef(null);
  const metricsRef   = useRef({ duration: 0, samples: [], cycles: 0, lastPeak: false, startTime: 0 });

  useEffect(() => { fetchBaseline(); return () => cleanup(); }, []);

  useEffect(() => { setCompositeScore(computeComposite(scores)); }, [scores]);

  const fetchBaseline = async () => {
    try {
      const res = await axios.get(`${API}/baseline`, { withCredentials: true });
      const recent = res.data.recent || [];
      if (recent.length > 0 && recent[0].practice_scores) setScores(recent[0].practice_scores);
      if (res.data.last_full_test) setLastFullTest(res.data.last_full_test);
    } catch (e) { /* no data yet - fine */ }
    finally { setLoading(false); }
  };

  const cleanup = () => {
    if (rafRef.current)   cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") audioCtxRef.current.close();
  };

  const startPractice = async (idx) => {
    if (measureState !== MS.IDLE) return;
    const practice = PRACTICES[idx];
    setActivePractice(idx);
    setMeasureState(MS.INTRO);
    setCountdown(3);
    setTimeLeft(practice.duration);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioCtxRef.current.createAnalyser();
      analyserRef.current.fftSize = 512;
      analyserRef.current.smoothingTimeConstant = 0.8;
      audioCtxRef.current.createMediaStreamSource(stream).connect(analyserRef.current);
    } catch (e) {
      toast.error("Microphone access required. Please allow mic and try again.");
      setMeasureState(MS.IDLE);
      setActivePractice(null);
      return;
    }

    let count = 3;
    const cd = setInterval(() => {
      count--;
      if (count <= 0) { clearInterval(cd); beginCapture(idx, practice.duration); }
      else setCountdown(count);
    }, 1000);
  };

  const beginCapture = (idx, durationSecs) => {
    setMeasureState(MS.ACTIVE);
    metricsRef.current = { duration: 0, samples: [], cycles: 0, lastPeak: false, startTime: Date.now() };
    let remaining = durationSecs;
    setTimeLeft(remaining);

    timerRef.current = setInterval(() => {
      remaining--;
      setTimeLeft(remaining);
      if (remaining <= 0) { clearInterval(timerRef.current); finishCapture(idx); }
    }, 1000);

    const practiceId = PRACTICES[idx].id;
    const analyze = () => {
      if (!analyserRef.current) return;
      const data = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      setAudioLevel(avg);
      const m = metricsRef.current;
      m.samples.push(avg);
      m.duration = (Date.now() - m.startTime) / 1000;
      if (practiceId === "kapalbhati") {
        if (avg > 20 && !m.lastPeak)  { m.cycles++; m.lastPeak = true; }
        else if (avg < 10) m.lastPeak = false;
      }
      rafRef.current = requestAnimationFrame(analyze);
    };
    rafRef.current = requestAnimationFrame(analyze);
  };

  const finishCapture = useCallback((idx) => {
    cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") audioCtxRef.current.close();

    const m   = metricsRef.current;
    const avg = m.samples.length ? m.samples.reduce((a, b) => a + b, 0) / m.samples.length : 0;
    const variance  = m.samples.length > 1
      ? m.samples.reduce((a, b) => a + Math.pow(b - avg, 2), 0) / m.samples.length : 0;
    const consistency = Math.max(0, Math.min(1, 1 - variance / 1000));
    const score = computeScore(PRACTICES[idx].id, { duration: m.duration, avgLevel: avg, consistency, cycles: m.cycles });

    setScores(prev => {
      const next = { ...prev, [PRACTICES[idx].id]: score };
      saveScores(next);
      return next;
    });
    setMeasureState(MS.DONE);
    setAudioLevel(0);
    setTimeout(() => { setMeasureState(MS.IDLE); setActivePractice(null); }, 1800);
  }, []);

  const saveScores = async (allScores) => {
    const composite = computeComposite(allScores);
    if (!composite) return;
    try {
      await axios.post(`${API}/baseline`, {
        duration_seconds: composite,
        measured_at: new Date().toISOString(),
        practice_scores: allScores,
        composite_score: composite,
        session_id: sessionId,
      }, { withCredentials: true });
    } catch (e) { console.error("Failed to save baseline:", e); }
  };

  const handleStartAll = () => {
    const nextIdx = PRACTICES.findIndex(p => !scores[p.id]);
    if (nextIdx >= 0 && measureState === MS.IDLE) startPractice(nextIdx);
  };

  const allComplete = PRACTICES.every(p => scores[p.id]);
  const isRunning   = measureState !== MS.IDLE;
  const today       = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  let daysLeft = 0;
  if (lastFullTest) {
    const next = new Date(lastFullTest);
    next.setDate(next.getDate() + 30);
    daysLeft = Math.max(0, Math.ceil((next - new Date()) / 86400000));
  }

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-10 h-10 border-[3px] border-t-transparent rounded-full animate-spin"
        style={{ borderColor: T.lock, borderTopColor: "transparent" }} />
    </div>
  );

  return (
    <div className="space-y-5 pb-24" data-testid="baseline-page">

      {/* HEADER */}
      <div>
        <p className="text-xs uppercase tracking-widest mb-1"
          style={{ color: T.lock, fontFamily: "'DM Mono', monospace" }}>
          Today - {today}
        </p>
        <h1 className="text-3xl font-bold tracking-tight"
          style={{ color: T.text, fontFamily: "'DM Serif Display', serif" }}>
          Baseline
        </h1>
        <p className="text-sm mt-1" style={{ color: T.muted }}>
          Your breath capacity, measured before practice.
        </p>
      </div>

      {/* COMPOSITE RING + PRACTICES */}
      <div className="rounded-2xl p-6" style={{ background: T.surface, border: `1px solid ${T.border}` }}>

        <div className="flex justify-center mb-6">
          <HeroRing score={compositeScore} />
        </div>

        <div className="space-y-1">
          {PRACTICES.map((practice, idx) => (
            <PracticeRow
              key={practice.id}
              practice={practice}
              score={scores[practice.id]}
              isActive={activePractice === idx}
              isRunning={isRunning}
              onClick={() => !isRunning && startPractice(idx)}
            />
          ))}
        </div>

        {/* Active measurement panel */}
        {activePractice !== null && isRunning && (
          <div className="mt-5 rounded-xl p-5 flex flex-col items-center gap-3"
            style={{
              background: `${PRACTICES[activePractice].color}0a`,
              border: `1px solid ${PRACTICES[activePractice].color}22`
            }}>
            <p className="text-xs uppercase tracking-widest"
              style={{ color: PRACTICES[activePractice].color, fontFamily: "'DM Mono', monospace" }}>
              {PRACTICES[activePractice].name}
            </p>

            {/* Mic circle */}
            <div className="relative flex items-center justify-center" style={{ width: 100, height: 100 }}>
              <svg width={100} height={100} style={{ transform: "rotate(-90deg)" }}>
                <circle cx={50} cy={50} r={44} fill="none" stroke={T.border2} strokeWidth={3} />
                {measureState === MS.ACTIVE && (
                  <circle cx={50} cy={50} r={44} fill="none"
                    stroke={PRACTICES[activePractice].color} strokeWidth={3} strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 44}
                    strokeDashoffset={2 * Math.PI * 44 * (1 - Math.min(audioLevel / 80, 1))}
                    style={{ filter: `drop-shadow(0 0 6px ${PRACTICES[activePractice].color}80)` }} />
                )}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {measureState === MS.INTRO && (
                  <>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 32, color: T.lock, lineHeight: 1 }}>
                      {countdown}
                    </span>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: T.muted,
                      textTransform: "uppercase", letterSpacing: "0.14em", marginTop: 4 }}>
                      Ready
                    </span>
                  </>
                )}
                {measureState === MS.ACTIVE && (
                  <>
                    <Mic className="w-5 h-5" style={{ color: PRACTICES[activePractice].color }} />
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, color: T.text, lineHeight: 1, marginTop: 4 }}>
                      {timeLeft}s
                    </span>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9,
                      color: PRACTICES[activePractice].color, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                      Active
                    </span>
                  </>
                )}
                {measureState === MS.DONE && (
                  <CheckCircle className="w-7 h-7" style={{ color: T.ground }} />
                )}
              </div>
            </div>

            <p className="text-xs text-center max-w-xs" style={{ color: T.muted }}>
              {PRACTICES[activePractice].instruction}
            </p>
            <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: T.dim }}>
              {PRACTICES[activePractice].measureNote}
            </p>
          </div>
        )}

        {/* CTA row */}
        <div className="mt-5 space-y-3">
          {!allComplete ? (
            <button
              onClick={handleStartAll}
              disabled={isRunning}
              className="w-full py-4 rounded-xl flex items-center justify-center gap-2 transition-opacity"
              style={{
                background: isRunning ? T.border : T.lock,
                color: isRunning ? T.dim : "#1a1200",
                fontFamily: "'DM Mono', monospace", fontSize: 12,
                letterSpacing: "0.08em", textTransform: "uppercase",
                opacity: isRunning ? 0.5 : 1, border: "none",
              }}
              data-testid="start-measurement-btn"
            >
              <div className={`w-2 h-2 rounded-full ${measureState === MS.ACTIVE ? "animate-ping" : ""}`}
                style={{ backgroundColor: isRunning ? T.dim : "#1a1200" }} />
              {isRunning ? "Measuring..." : "Run Daily Check - ~2 min"}
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <div className="flex-1 flex items-center gap-2 py-3 px-4 rounded-xl"
                style={{ background: `${T.ground}0d`, border: `1px solid ${T.ground}33` }}>
                <CheckCircle className="w-4 h-4" style={{ color: T.ground }} />
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: T.ground }}>
                  Daily check complete
                </span>
              </div>
              <button onClick={() => setScores({})}
                className="py-3 px-4 rounded-xl flex items-center justify-center"
                style={{ background: T.border, border: "none", color: T.muted }}
                data-testid="retry-measurement-btn">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          )}
          <p className="text-center" style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: T.dim }}>
            {daysLeft > 0 ? `Next full baseline - ${daysLeft} days` : "Full baseline available"}
          </p>
        </div>
      </div>

      {/* TIP */}
      <div className="rounded-xl p-4 flex items-start gap-3"
        style={{ background: T.surface, border: `1px solid ${T.border}` }}>
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: T.lock }} />
        <p className="text-sm" style={{ color: T.muted }}>
          Practise in a quiet space. The microphone detects breath rhythm, not volume.
          Each practice takes 30-40 seconds. Tap any row to run individually or use the button to run all three in sequence.
        </p>
      </div>

      {/* BIOFEEDBACK / HR MONITOR */}
      {biofeedback.isSupported && (
        <div className="rounded-2xl" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
          <div className="flex items-center justify-between px-5 py-4"
            style={{ borderBottom: biofeedback.isConnected ? `1px solid ${T.border}` : "none" }}>
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4" style={{ color: biofeedback.isConnected ? T.ground : T.muted }} />
              <span style={{ fontFamily: "Instrument Sans, sans-serif", fontSize: 15, fontWeight: 500, color: T.text }}>
                Biofeedback
              </span>
            </div>
            {!biofeedback.isConnected ? (
              <button
                onClick={biofeedback.connect}
                disabled={biofeedback.isConnecting || biofeedback.isReconnecting}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{ border: `1px solid ${T.observe}4d`, color: T.observe, background: "transparent",
                  fontFamily: "'DM Mono', monospace", fontSize: 11, opacity: (biofeedback.isConnecting || biofeedback.isReconnecting) ? 0.6 : 1 }}
                data-testid="connect-biofeedback-btn"
              >
                {(biofeedback.isConnecting || biofeedback.isReconnecting)
                  ? <div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin mr-1"
                      style={{ borderColor: T.observe, borderTopColor: "transparent" }} />
                  : <Bluetooth className="w-3 h-3 mr-1" />
                }
                {biofeedback.isConnecting ? "Connecting..." : biofeedback.isReconnecting ? "Reconnecting..." : "Connect HR Monitor"}
              </button>
            ) : (
              <button
                onClick={biofeedback.disconnect}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{ border: `1px solid ${T.ground}4d`, color: T.ground, background: "transparent",
                  fontFamily: "'DM Mono', monospace", fontSize: 11 }}
                data-testid="disconnect-biofeedback-btn"
              >
                <BluetoothConnected className="w-3 h-3 mr-1" />
                {biofeedback.deviceName}
              </button>
            )}
          </div>

          {!biofeedback.isConnected ? (
            <div className="px-5 py-6 text-center">
              <Bluetooth className="w-7 h-7 mx-auto mb-3" style={{ color: T.dim }} />
              <p className="text-sm" style={{ color: T.muted }}>
                Connect a heart rate monitor to replace the HRV estimate with a live reading,
                upgrading your neuroplasticity calculation from estimated to measured.
              </p>
              <p className="text-xs mt-2" style={{ color: T.dim, fontFamily: "'DM Mono', monospace" }}>
                Compatible with Polar, Garmin, Wahoo, and other BLE heart rate monitors.
              </p>
              {biofeedback.error && (
                <p className="text-xs mt-2" style={{ color: T.danger }}>{biofeedback.error}</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3">
              {[
                { label: "Heart Rate",  value: biofeedback.heartRate,         unit: "BPM", icon: Heart,     color: "#f472b6", testId: "heart-rate-display" },
                { label: "HRV (RMSSD)", value: biofeedback.hrv,               unit: "ms",  icon: Zap,       color: T.connect, testId: "hrv-display" },
                { label: "Avg HR",      value: biofeedback.getAverageHR?.(),  unit: "BPM", icon: Activity,  color: T.observe },
              ].map(({ label, value, unit, icon: Icon, color, testId }, i) => (
                <div key={label} className="flex flex-col items-center py-5 px-3 text-center"
                  style={{ borderRight: i < 2 ? `1px solid ${T.border}` : "none" }}>
                  <div className="flex items-center gap-1 mb-2">
                    <Icon className="w-4 h-4" style={{ color: value ? color : T.dim }} />
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: T.dim,
                      textTransform: "uppercase", letterSpacing: "0.1em" }}>
                      {label}
                    </span>
                  </div>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 28, color: value ? color : T.dim, lineHeight: 1 }}
                    data-testid={testId}>
                    {value || "-"}
                  </span>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: T.dim, marginTop: 2 }}>
                    {unit}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default Baseline;
